export const THEME_MODES = ['light', 'dark', 'system'] as const

export type ThemeMode = (typeof THEME_MODES)[number]

export type ThemeLayout = 'app' | 'auth' | 'marketing'

export const GUEST_STORAGE_KEY = 'app_theme_preference'

export const LEGACY_STORAGE_KEY = 'theme'

export function userStorageKey(userId?: number | null): string {
  if (userId != null) {
    return `${GUEST_STORAGE_KEY}_${userId}`
  }
  return GUEST_STORAGE_KEY
}

export function normalizeThemeMode(value: unknown): ThemeMode {
  let resolved = value

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      try {
        resolved = JSON.parse(trimmed)
      } catch {
        resolved = trimmed.slice(1, -1)
      }
    }
  }

  if (typeof resolved === 'string' && (THEME_MODES as readonly string[]).includes(resolved)) {
    return resolved as ThemeMode
  }

  return 'system'
}

export function resolveIsDark(mode: ThemeMode, prefersDark: boolean): boolean {
  if (mode === 'dark') return true
  if (mode === 'light') return false
  return prefersDark
}

export function isMarketingPath(path: string): boolean {
  return path === '/' || path.startsWith('/marketing')
}

export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function readStoredPreference(storageKey: string): ThemeMode | null {
  const raw = localStorage.getItem(storageKey)
  if (!raw) return null
  return normalizeThemeMode(raw)
}

export function readGuestPreference(): ThemeMode | null {
  const guest = readStoredPreference(GUEST_STORAGE_KEY)
  if (guest) return guest

  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
  if (!legacy) return null
  return normalizeThemeMode(legacy)
}

export function resolveStorageKeyForBoot(): string {
  if (typeof localStorage === 'undefined') {
    return GUEST_STORAGE_KEY
  }

  try {
    const userRaw = localStorage.getItem('user')
    if (userRaw) {
      const user = JSON.parse(userRaw) as { id?: number }
      if (user?.id != null) {
        return userStorageKey(user.id)
      }
    }
  } catch {
    // Ignorer les données utilisateur invalides
  }

  return GUEST_STORAGE_KEY
}

export function applyDarkClass(isDark: boolean): void {
  if (typeof document === 'undefined') return

  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export function resolveLayoutFromRoute(meta?: { layout?: string }): ThemeLayout {
  if (meta?.layout === 'marketing') return 'marketing'
  if (meta?.layout === 'auth') return 'auth'
  return 'app'
}

export function resolveDomIsDark(
  layout: ThemeLayout,
  mode: ThemeMode,
  prefersDark: boolean
): boolean {
  if (layout === 'marketing') return false
  return resolveIsDark(mode, prefersDark)
}
